import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { RobotListItem, useRobots } from "@/entities/robot";
import {
  MarketFilter,
  readMarketFilter,
  writeMarketFilter,
  type MarketFilterValue,
} from "@/features/catalog-filter";
import { ROUTES } from "@/shared/config";
import { staggerIndex } from "@/shared/lib";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";
import { TopBar } from "@/widgets/top-bar";

/**
 * Каталог. Figma: `Robots · Catalog` (20:2).
 *
 * Список грузится целиком и один раз, фильтр режет его на месте. Раньше
 * `market` уходил в `GET /robots` параметром, и каждый срез был отдельным
 * ключом кэша — переключение туда-сюда в сеть не ходило, но первый показ
 * каждого среза ждал ответа и мигал скелетоном. Роботов пять (ТЗ §4), и
 * возить их тремя запросами вместо одного незачем.
 *
 * Серверный `?market=` в контракте остался: им пользуются другие клиенты, и
 * убирать рабочий параметр ради того, что его не зовёт один экран, — не повод.
 *
 * Выбор фильтра переживает уход на карточку робота (`features/catalog-filter`).
 */
export function RobotsCatalogPage() {
  const { t } = useTranslation();
  const [market, setMarket] = useState<MarketFilterValue>(readMarketFilter);
  const { data, isPending, isError, refetch } = useRobots();

  const visible = useMemo(
    // Сравнение с сырым `robot.market`: незнакомое значение просто не совпадёт
    // ни со «spot», ни с «futures» и останется видимым в «Все». Каталог от
    // кривых данных одного робота не ломается (ТЗ №1).
    () => {
      const all = data ?? [];
      return market === "all" ? all : all.filter((robot) => robot.market === market);
    },
    [data, market],
  );

  return (
    <div className="flex flex-col gap-3 px-5 py-3">
      <TopBar title={t("screens.robots")} subtitle={t("robots.subtitle")} />

      <div className="flex items-center gap-3">
        <MarketFilter
          value={market}
          onChange={(next) => {
            setMarket(next);
            writeMarketFilter(next);
          }}
        />
        <Link
          to={ROUTES.robotsCompare}
          className="shrink-0 text-caption font-semibold text-action-primary"
        >
          {t("robots.compare")} →
        </Link>
      </div>

      {isPending ? (
        <ScreenSkeleton blocks={[122, 122, 122]} />
      ) : isError ? (
        <ScreenState
          tone="loss"
          icon="warningFill"
          title={t("states.error.title")}
          body={t("states.error.body")}
          action={{ label: t("states.error.action"), onClick: () => void refetch() }}
        />
      ) : visible.length === 0 ? (
        <ScreenState tone="info" icon="robotFill" title={t("robots.empty.title")} body={t("robots.empty.body")} />
      ) : (
        <ul className="flex flex-col gap-3">
          {visible.map((robot, index) => (
            <li key={robot.id} className="stagger" style={staggerIndex(index)}>
              <RobotListItem robot={robot} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
